import React from 'react';
import { Table, Tag } from 'antd';
import { Eye, Pencil, TriangleAlert, Calendar, FileText } from 'lucide-react';
import dayjs from 'dayjs';
import { SectionCard, StatusTag, IconAction, IconActions, EmptyState } from '../../common';
import { CONTRACT_TYPE_FILTERS, getContractType } from '../../../utils/contractTypes';
import { formatCurrency, formatNumber } from '../../../utils/format';
import { isExpired, recordStatus } from '../../../utils/recordStatus';

function ContractsTable({ rows, loading, navigateContract, onEdit }) {
    const columns = [
        {
            title: 'Contratación',
            dataIndex: 'name',
            key: 'name',
            render: (text, record) => (
                <div className="flex flex-col items-start gap-1 py-0.5">
                    <button
                        type="button"
                        onClick={() => navigateContract(record._id)}
                        className="p-0 bg-transparent border-0 cursor-pointer text-left text-sm font-medium text-primary-600 hover:text-primary-700 hover:underline transition-colors">
                        {text || 'N/A'}
                    </button>
                    {isExpired(record.expirationDate) && (
                        <StatusTag tone="warning" icon={<TriangleAlert />}>
                            Expirado
                        </StatusTag>
                    )}
                </div>
            ),
            sorter: (a, b) => (a.name || '').localeCompare(b.name || '')
        },
        {
            title: 'Tipo',
            dataIndex: 'type',
            key: 'type',
            render: (type) => {
                const contractType = getContractType(type);
                return (
                    <Tag color={contractType.color} bordered={false} className="!m-0">
                        {contractType.label}
                    </Tag>
                );
            },
            filters: CONTRACT_TYPE_FILTERS,
            onFilter: (value, record) => record.type === value
        },
        {
            // `visibleStoreCount` refleja los pisos que pasan los filtros de canal / cliente / PDV
            title: 'Pisos de Venta',
            dataIndex: 'stores',
            key: 'stores',
            align: 'right',
            render: (stores, record) => (
                <span className="tabular-nums text-surface-700">
                    {formatNumber(record.visibleStoreCount ?? (Array.isArray(stores) ? stores.length : stores || 0))}
                    <span className="ml-1 text-surface-400">pisos</span>
                </span>
            )
        },
        {
            title: 'Inversión',
            key: 'investment',
            align: 'right',
            render: (_, record) => <span className="tabular-nums font-semibold text-surface-900">{formatCurrency(record.investment ?? 0)}</span>,
            sorter: (a, b) => (a.investment ?? 0) - (b.investment ?? 0)
        },
        {
            title: 'Estado',
            key: 'status',
            render: (_, record) => {
                const status = recordStatus(record);
                return (
                    <StatusTag tone={status.tone} dot>
                        {status.label}
                    </StatusTag>
                );
            },
            filters: [
                { text: 'Activo', value: 'active' },
                { text: 'Expirado', value: 'expired' },
                { text: 'Inactivo', value: 'inactive' }
            ],
            onFilter: (value, record) => {
                if (value === 'expired') return isExpired(record.expirationDate);
                if (value === 'active') return record.active && !isExpired(record.expirationDate);
                if (value === 'inactive') return !record.active;
                return true;
            }
        },
        {
            title: 'Vencimiento',
            dataIndex: 'expirationDate',
            key: 'expirationDate',
            render: (date) => {
                const expired = isExpired(date);
                return (
                    <span className={`inline-flex items-center gap-1.5 tabular-nums ${expired ? 'text-danger-fg font-medium' : 'text-surface-600'}`}>
                        <Calendar className={`text-[13px] ${expired ? '' : 'text-surface-400'}`} />
                        {date ? dayjs(date).format('DD/MM/YYYY') : 'N/A'}
                    </span>
                );
            },
            sorter: (a, b) => new Date(a.expirationDate) - new Date(b.expirationDate)
        },
        {
            title: 'Acciones',
            key: 'actions',
            align: 'right',
            width: 100,
            render: (_, record) => (
                <IconActions>
                    <IconAction icon={<Eye />} label="Ver" onClick={() => navigateContract(record._id)} />
                    <IconAction icon={<Pencil />} label="Editar" onClick={() => onEdit(record)} />
                </IconActions>
            )
        }
    ];

    return (
        <SectionCard title="Contrataciones" icon={<FileText />} flush>
            <Table
                columns={columns}
                dataSource={rows}
                loading={loading}
                rowKey="_id"
                size="middle"
                scroll={{ x: 'max-content' }}
                locale={{
                    emptyText: (
                        <EmptyState
                            icon={<FileText />}
                            title="No hay contrataciones"
                            description="Ajusta los filtros o crea una nueva contratación."
                        />
                    )
                }}
                pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    showTotal: (total) => `Total ${total} contrataciones`
                }}
            />
        </SectionCard>
    );
}

export default ContractsTable;
